import { SpoolDto } from '../../../types/spool-local';
import { CSSProperties, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WidgetsParams } from '..';
import { ErrorModalIcon } from '../../../assets/icons';
import appContext from '../../../context/app/appContext';
import { appendStyles } from '../../../helpers/domManipulations';
import { formatAmountWithSuffix, formatPercentages } from '../../../helpers/formats';
import useIsMounted from '../../../hooks/useIsMounted';

type Props = {
    address: string;
};

type WidgetStyleType = Record<string, CSSProperties>;

const SPOOL_WIDGET_APP_ELEMENTS: { [k: string]: string } = {
    container: 'spool-widget__app',
    title: 'spool-widget__title',
    statisticsTitle: 'spool-widget__statistics__title',
    statisticsDetail: 'spool-widget__statistics__details',
    deposit: 'spool-widget__deposit',
};

const SpoolWidget: React.FC<Props> = ({ address }) => {
    const [spoolById, setSpoolById] = useState<SpoolDto>();

    const { sdk } = useContext(appContext);
    const [isLoading, setIsLoading] = useState<Boolean>(true);

    const isMounted = useIsMounted();

    const { spoolWidgetStyling } = useParams<WidgetsParams>();

    const spoolWidgetStylingParams = useMemo(
        () => Object.fromEntries(new URLSearchParams(spoolWidgetStyling)),
        [spoolWidgetStyling]
    );

    const spoolWidgetStyles = useMemo(
        () =>
            Object.entries(spoolWidgetStylingParams).reduce(
                (widgetStyles: WidgetStyleType, [key, value]) => {
                    const spoolWidgetStyle = value
                        .split(';')
                        .reduce((styleObject: { [k: string]: string }, currentStyle) => {
                            const styleParts = currentStyle.split(':');
                            if (styleParts[0] && styleParts[1]) {
                                styleObject[styleParts[0]] = styleParts[1];
                            }
                            return styleObject;
                        }, {});

                    widgetStyles[key] = spoolWidgetStyle;
                    return widgetStyles;
                },
                {}
            ),
        [spoolWidgetStylingParams]
    );

    useEffect(() => {
        Object.entries(spoolWidgetStyles).forEach(([className, styling]) => {
            const elements = Array.from(
                document.getElementsByClassName(
                    SPOOL_WIDGET_APP_ELEMENTS[className]
                ) as HTMLCollectionOf<HTMLElement>
            );
            appendStyles(elements, styling);
        });
    }, [spoolWidgetStyles, isLoading]);

    useEffect(() => {
        if (!sdk) return;

        const getSpoolById = async () => {
            try {
                const spoolById = await sdk.spools.getSpoolById(address);
                isMounted() && setSpoolById(spoolById);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        getSpoolById();
    }, [sdk, address, isMounted]);

    return (
        <div className='spool-widget__app'>
            {isLoading ? (
                <div className='spool-widget__loading'>Retrieving data...</div>
            ) : spoolById ? (
                <>
                    <span className='spool-widget__title'>{spoolById.name}</span>
                    <div className='spool-widget__statistics__container'>
                        <div className='spool-widget__statistics'>
                            <span className='spool-widget__statistics__title'>APY</span>
                            <span className='spool-widget__statistics__details'>
                                {formatPercentages(
                                    parseFloat(spoolById.adjustedApy) * 100
                                )}
                                %
                            </span>
                        </div>
                        <div className='spool-widget__statistics'>
                            <span className='spool-widget__statistics__title'>TVR</span>
                            <span className='spool-widget__statistics__details'>
                                ~{formatAmountWithSuffix(spoolById.tvr)}$
                            </span>
                        </div>
                    </div>
                    <div
                        className='spool-widget__deposit'
                        onClick={() =>
                            window.open(`https://www.app.spool.fi/app/spool/${address}`)
                        }
                    >
                        Deposit
                    </div>
                </>
            ) : (
                <div className='spool-widget__error'>
                    <ErrorModalIcon />
                    <span>Error/Invalid Spool Address...</span>
                </div>
            )}
        </div>
    );
};

export default SpoolWidget;
